import { Suspense, lazy } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Confirmation as QuickConfirmation } from "components/pages/quickPay/confirmation/Confirmation";
import { AccountInfo } from "components/pages/quickPay/accountInfo/AccountInfo";
import { Payment as QuickPayment } from "components/pages/quickPay/payment/Payment";
import { Review } from "components/pages/quickPay/review/Review";

import { Registration } from "components/pages/account/registration/Registration";
import { Create } from "components/pages/account/registration/create/Create";
import { Final } from "components/pages/account/registration/final/Final";
import { Verification } from "components/pages/account/verification/Verification";

import { Login } from "components/pages/account/login/Login";
import { LoginRedirect } from "components/pages/account/login/LoginRedirect";
import { Main } from "components/pages/account/login/main/Main";
import { AccountSummary } from "components/pages/dashboard/accountSummary/AccountSummary";
import { Settings } from "components/pages/dashboard/settings/Settings";
import { DashboardMain } from "components/pages/dashboard/dashboard/DashboardMain";

import { useRootSelector } from "store/hooks";
import { ClientError } from "components/pages/clientError/ClientError";
import { PaymentActivity } from "components/widgets/paymentActivity/paymentActivity";
import { AuthenticationGuard } from "../auth0/authentication-guard";
import ExpiredImagePanel from "components/pages/quickPay/expired/ExpiredImagePanel";
import ExpiredMessagePanel from "components/pages/quickPay/expired/ExpiredMessagePanel";

const Dashboard = lazy(() =>
  import("components/pages/dashboard/Dashboard").then(({ Dashboard }) => ({
    default: Dashboard,
  })),
);

export const MainRoute = () => {
  const { t } = useTranslation(["app"]);
  const { paymentCenter, client } = useRootSelector((state) => state.config);

  let location = useLocation();

  const createClientErrorElement = () => {
    return (
      <Suspense fallback={<ft-loader></ft-loader>}>
        <ClientError />
      </Suspense>
    );
  };

  // Map of page titles - NOTE keeping this all here for now but may move to
  // its own file later
  const titleMap: any = {
    "/dashboard": `${t("app:routing_dashboard_label")}`,
    "/dashboard/settings": `${t("app:routing_settings_label")}`,
    "/dashboard/payments": `${t("app:routing_payment_activity_label")}`,
    "/dashboard/account-summary": `${t("app:routing_account_summary_label")}`,
    "/registration": `${t("app:routing_registration_begin")}`,
    "/registration/final": `${t("app:routing_registration_end")}`,
    "/verification": `${t("app:routing_verification_label")}`,
    "/quick-info": `${t("app:routing_quickpay_account_info")}`,
    "/quick-payment": `${t("app:routing_quickpay_payment")}`,
    "/quick-review": `${t("app:routing_quickpay_review")}`,
    "/quick-confirmation": `${t("app:routing_quickpay_confirmation")}`,
  };

  // Set the page title based on routing for 508 compliance and to provide information to

  let currentTitle = "";

  const appPath = location.pathname.split(paymentCenter.customerKey!);
  if (appPath[1]) {
    if (titleMap[`${appPath[1]}`]) {
      currentTitle = titleMap[`${appPath[1]}`];
    } else {
      currentTitle = t("app:routing_not_found");
    }
  } else {
    currentTitle = `${t("app:routing_login")}`;
  }

  document.title = currentTitle || client.defaultApplicationName!;

  return (
    <Routes>
      <>
        <Route
          path="dashboard"
          element={<AuthenticationGuard component={Dashboard} />}
        >
          <Route index element={<DashboardMain />}></Route>
          <Route path="settings" element={<Settings />}></Route>
          <Route path="payments" element={<PaymentActivity />}></Route>
          <Route path="account-summary" element={<AccountSummary />}></Route>
        </Route>
        <Route path="registration" element={<Registration />}>
          <Route index element={<Create />}></Route>
          <Route path="final" element={<Final />}></Route>
        </Route>
        <Route path="verification" element={<Verification />}></Route>
        <Route path="" element={<Login />}>
          <Route index element={<Main />}></Route>
          <Route path="login" element={<LoginRedirect />}></Route>
        </Route>
        <Route path="expired" element={<ExpiredImagePanel />}>
          <Route index element={<ExpiredMessagePanel />}></Route>
        </Route>
      </>
      {paymentCenter.quickPayments.enabled && (
        <>
          <Route path="quick-info" element={<AccountInfo />}></Route>
          <Route path="quick-payment" element={<QuickPayment />}></Route>
          <Route path="quick-review" element={<Review />}></Route>
          <Route
            path="quick-confirmation"
            element={<QuickConfirmation />}
          ></Route>
        </>
      )}
      <Route path="error" element={createClientErrorElement()}></Route>
    </Routes>
  );
};
